import React, { useState, useEffect } from 'react';
import { Table, Card, Space, DatePicker, Select, Button, Typography, Tag, Avatar, message, Statistic, Row, Col } from 'antd';
import { HistoryOutlined, SearchOutlined, LoginOutlined, LogoutOutlined, ReloadOutlined, CarOutlined, DollarOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

interface ParkingLog {
    Id: number;
    CardNumber: string;
    InOutStatus: 'IN' | 'OUT';
    LogTime: string;
    GateName: string;
    Fee: number;
    VehicleType: string;
    LicensePlate: string;
    StudentName: string;
    CurrentBalance: number;
}

interface PaginationInfo {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

const ParkingLogsPage: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<ParkingLog[]>([]);
    const [pagination, setPagination] = useState<PaginationInfo>({
        total: 0,
        page: 1,
        limit: 20,
        totalPages: 0
    });
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
        dayjs().startOf('day'),
        dayjs().endOf('day')
    ]);
    const [location, setLocation] = useState<string>('');
    const [eventType, setEventType] = useState<string>('');

    const fetchData = async (page: number = 1) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();

            if (dateRange && dateRange[0] && dateRange[1]) {
                params.append('startDate', dateRange[0].toISOString());
                params.append('endDate', dateRange[1].toISOString());
            }

            if (location) {
                params.append('location', location);
            }

            if (eventType) {
                params.append('eventType', eventType);
            }

            params.append('page', String(page));
            params.append('limit', String(pagination.limit));

            const response = await axios.get(`/api/v1/parking/admin/logs?${params.toString()}`);

            if (response.data.success) {
                setData(response.data.data);
                setPagination(response.data.pagination);
            } else {
                message.error('Không thể tải nhật ký gửi xe');
            }
        } catch (error) {
            console.error('Error fetching parking logs:', error);
            message.error('Lỗi khi tải dữ liệu');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleFilter = () => {
        fetchData(1);
    };

    const handleTableChange = (newPagination: any) => {
        fetchData(newPagination.current);
    };

    const columns = [
        {
            title: 'Thời gian',
            dataIndex: 'LogTime',
            key: 'LogTime',
            width: 180,
            render: (val: string) => dayjs(val).format('DD/MM/YYYY HH:mm:ss')
        },
        {
            title: 'Trạng thái',
            dataIndex: 'InOutStatus',
            key: 'InOutStatus',
            width: 120,
            render: (status: string) => (
                <Tag
                    color={status === 'IN' ? 'green' : 'orange'}
                    icon={status === 'IN' ? <LoginOutlined /> : <LogoutOutlined />}
                >
                    {status === 'IN' ? 'VÀO' : 'RA'}
                </Tag>
            )
        },
        {
            title: 'Họ tên',
            dataIndex: 'StudentName',
            key: 'StudentName',
            render: (name: string) => name || '—'
        },
        {
            title: 'MSSV',
            dataIndex: 'CardNumber',
            key: 'CardNumber'
        },
        {
            title: 'Biển số',
            dataIndex: 'LicensePlate',
            key: 'LicensePlate',
            render: (plate: string) => plate || '—'
        },
        {
            title: 'Cổng',
            dataIndex: 'GateName',
            key: 'GateName',
            render: (gate: string) => gate || 'Cổng chính'
        },
        {
            title: 'Phí',
            dataIndex: 'Fee',
            key: 'Fee',
            width: 100,
            align: 'right' as const,
            render: (fee: number) => fee ? `${fee.toLocaleString()}đ` : '—'
        },
        {
            title: 'Số dư',
            dataIndex: 'CurrentBalance',
            key: 'CurrentBalance',
            width: 120,
            align: 'right' as const,
            render: (balance: number) => balance ? `${balance.toLocaleString()}đ` : '—'
        }
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Card
                title={
                    <Space>
                        <HistoryOutlined />
                        <span>Nhật ký xe vào ra hàng ngày</span>
                    </Space>
                }
                extra={
                    <Space>
                        <Text type="secondary">
                            Tổng: {pagination.total} lượt
                        </Text>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => fetchData(pagination.page)}
                            loading={loading}
                        >
                            Làm mới
                        </Button>
                    </Space>
                }
            >
                <div style={{ marginBottom: 16 }}>
                    <Space wrap>
                        <RangePicker
                            value={dateRange}
                            onChange={(dates) => {
                                if (dates && dates[0] && dates[1]) {
                                    setDateRange([dates[0], dates[1]]);
                                }
                            }}
                            format="DD/MM/YYYY"
                            placeholder={['Từ ngày', 'Đến ngày']}
                        />
                        <Select
                            placeholder="Loại sự kiện"
                            style={{ width: 150 }}
                            value={eventType || undefined}
                            onChange={setEventType}
                            allowClear
                        >
                            <Select.Option value="IN">Vào bãi</Select.Option>
                            <Select.Option value="OUT">Ra bãi</Select.Option>
                        </Select>
                        <Select
                            placeholder="Chọn cổng"
                            style={{ width: 150 }}
                            value={location || undefined}
                            onChange={setLocation}
                            allowClear
                        >
                            <Select.Option value="Cổng chính">Cổng chính</Select.Option>
                            <Select.Option value="Cổng phụ">Cổng phụ</Select.Option>
                        </Select>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleFilter}
                            loading={loading}
                        >
                            Lọc dữ liệu
                        </Button>
                    </Space>
                </div>
                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="Id"
                    loading={loading}
                    pagination={{
                        current: pagination.page,
                        pageSize: pagination.limit,
                        total: pagination.total,
                        showSizeChanger: false,
                        showTotal: (total) => `Tổng ${total} lượt`
                    }}
                    onChange={handleTableChange}
                    scroll={{ x: 1200 }}
                />
            </Card>
        </div>
    );
};

export default ParkingLogsPage;
